<template>
  <div class="withdraw-page">
    <div class="background-header"></div>
    <div class="shell">
      <app-topbar @lang-change="lang = $event" />

      <section class="balance-card" @click="$router.push('/balance-logs')" style="cursor:pointer">
        <div class="balance-card-inner"></div>
        <div class="balance-content">
          <div class="balance-label">{{ t.availableBalance }}</div>
          <div class="balance-amount">{{ availableBalance }} OC</div>
          <div class="balance-notice">
            <span class="notice-text">{{ t.notice }}</span>
          </div>
        </div>
      </section>

      <section class="info-section">
        <div class="info-section-label">{{ t.confirmInfo }}</div>
      </section>

      <section class="detail-card">
        <div class="detail-row input-row">
          <span class="detail-label">{{ t.applyAmount }}</span>
          <div class="input-wrap">
            <input class="amount-input" type="number" v-model.number="inputAmount" min="0" step="any" placeholder="0.00" @input="clampInput" />
            <span class="input-unit">OC</span>
          </div>
        </div>
        <div class="detail-row">
          <span class="detail-label">{{ t.receiveAmount }}</span>
          <span class="detail-value">{{ receiveAmount }} OC</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">{{ t.fee }}</span>
          <span class="detail-value">3%</span>
        </div>
      </section>

      <button class="confirm-btn" type="button" :disabled="withdrawing" @click="onConfirmWithdraw">
        <img class="confirm-btn-bg" src="@/assets/home/btn-connect-wallet.png" alt="" />
        <span class="confirm-btn-text" v-if="!withdrawing">{{ t.confirmWithdraw }}</span>
        <span class="confirm-btn-text" v-else>Processing...</span>
      </button>

      <button class="confirm-btn reinvest-btn" type="button" :disabled="!canReinvest100 || reinvesting" @click="showReinvestModal = true">
        <img class="confirm-btn-bg" src="@/assets/home/btn-connect-wallet.png" alt="" />
        <span class="confirm-btn-text" v-if="!reinvesting">{{ t.reinvest100 }}</span>
        <span class="confirm-btn-text" v-else>Processing...</span>
      </button>

    </div>

    <bottom-nav active-tab="withdraw" />

    <div class="modal-mask" v-if="showReinvestModal" @click.self="showReinvestModal = false">
      <div class="modal-card">
        <div class="modal-title">{{ t.reinvest100 }}</div>
        <div class="modal-hint">{{ t.reinvest100Hint }}</div>
        <div class="modal-actions">
          <button class="modal-btn modal-btn--cancel" type="button" @click="showReinvestModal = false">{{ t.cancel }}</button>
          <button class="modal-btn modal-btn--confirm" type="button" :disabled="reinvesting" @click="onConfirmReinvest100">
            <img class="modal-btn-bg" src="@/assets/home/btn-connect-wallet.png" alt="" />
            <span class="modal-btn-text" v-if="!reinvesting">{{ t.confirm }}</span>
            <span class="modal-btn-text" v-else>Processing...</span>
          </button>
        </div>
      </div>
    </div>

    <div class="toast" v-if="toastVisible">{{ toastMsg }}</div>
  </div>
</template>

<script>
import AppTopbar from '@/components/AppTopbar.vue'
import BottomNav from '@/components/BottomNav.vue'
import { wallet } from '@/store/wallet'
import { getUser, submitWithdrawal, reinvest100 } from '@/api/auth'
import { getMessages } from '@/i18n'
import { ethers } from 'ethers'
import RouterAbi from '@/abi/Router.json'

const OC_ADDRESS = process.env.VUE_APP_OC_ADDRESS
const ALK_ADDRESS = process.env.VUE_APP_ALK_ADDRESS
const USDT_ADDRESS = process.env.VUE_APP_USDT_ADDRESS
const ROUTER_ADDRESS = process.env.VUE_APP_ROUTER_ADDRESS
const RPC_URL = process.env.VUE_APP_NEXT_PUBLIC_NODE_1

const RECEIVE_RATIO = 0.97

export default {
  name: 'WithdrawPage',
  components: { AppTopbar, BottomNav },
  data() {
    return {
      lang: localStorage.getItem('lang') || 'en',
      availableBalance: '--',
      inputAmount: '',
      rawBalance: null,
      ocPrice: null,
      withdrawing: false,
      reinvesting: false,
      showReinvestModal: false,
      toastVisible: false,
      toastMsg: '',
    }
  },
  computed: {
    t() {
      return getMessages(this.lang)
    },
    walletAddress() {
      return wallet.address
    },
    isLoggedIn() {
      return wallet.isLoggedIn
    },
    receiveAmount() {
      const oc = parseFloat(this.inputAmount)
      if (!oc || oc <= 0) return '--'
      return (oc * RECEIVE_RATIO).toFixed(2)
    },
    canReinvest100() {
      if (!this.rawBalance || !this.ocPrice) return false
      return this.rawBalance * this.ocPrice >= 100
    },
  },
  watch: {
    walletAddress(addr) {
      this.resetUserData()
      if (addr && wallet.isLoggedIn) this.loadUserData()
    },
    isLoggedIn(val) {
      if (val) this.loadUserData()
    },
  },
  mounted() {
    this.lang = localStorage.getItem('lang') || 'en'
    this.fetchOcPrice()
    if (this.walletAddress && wallet.isLoggedIn) this.loadUserData()
  },
  methods: {
    resetUserData() {
      this.availableBalance = '--'
      this.inputAmount = ''
      this.rawBalance = null
    },
    async loadUserData() {
      try {
        const res = await getUser()
        const u = res.data
        this.rawBalance = u.balance ? parseFloat(u.balance) : null
      } catch (e) {
        // not logged in
      }
      this.updateBalance()
    },
    updateBalance() {
      if (this.rawBalance === null) return
      this.availableBalance = this.rawBalance.toFixed(2)
    },
    clampInput() {
      if (this.inputAmount !== '' && this.inputAmount !== null && Number(this.inputAmount) < 0) {
        this.inputAmount = ''
      }
    },
    async onConfirmWithdraw() {
      if (!this.walletAddress) {
        alert(this.t.connectFirst)
        return
      }
      const oc = parseFloat(this.inputAmount)
      if (!oc || oc <= 0) {
        this.showToast('Invalid amount')
        return
      }
      if (this.rawBalance !== null && oc > this.rawBalance) {
        this.showToast('Insufficient balance')
        return
      }
      this.withdrawing = true
      try {
        await submitWithdrawal(oc.toFixed(2))
        this.showToast(this.t.withdrawSuccess)
        this.inputAmount = ''
        this.loadUserData()
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.error
        this.showToast(msg || 'Error')
      } finally {
        this.withdrawing = false
      }
    },
    async fetchOcPrice() {
      if (!OC_ADDRESS || !ALK_ADDRESS || !USDT_ADDRESS || !ROUTER_ADDRESS) return
      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL)
        const router = new ethers.Contract(ROUTER_ADDRESS, RouterAbi, provider)
        const amounts = await router.getAmountsOut(ethers.parseUnits('1', 18), [OC_ADDRESS, ALK_ADDRESS, USDT_ADDRESS])
        this.ocPrice = parseFloat(ethers.formatUnits(amounts[2], 18))
      } catch (e) {
        console.error('fetchOcPrice failed', e)
      }
    },
    async onConfirmReinvest100() {
      this.reinvesting = true
      try {
        await reinvest100()
        this.showReinvestModal = false
        this.showToast(this.t.reinvestSuccess)
        this.loadUserData()
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.error
        this.showToast(msg || 'Error')
      } finally {
        this.reinvesting = false
      }
    },
    showToast(msg) {
      this.toastMsg = msg
      this.toastVisible = true
      clearTimeout(this._toastTimer)
      this._toastTimer = setTimeout(() => {
        this.toastVisible = false
      }, 2500)
    },
  },
}
</script>

<style lang="less" scoped>
.withdraw-page {
  min-height: 100vh;
  color: #fff;
  background-image: url(../assets/home/background.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */
  padding-bottom: 90px;
  position: relative;
}

.background-header {
  width: 100%;
  height: 200px;
  background-image: url(../assets/home/background-header.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
}

.shell {
  max-width: 430px;
  margin: 0 auto;
  padding: 0 15px 24px;
  box-sizing: border-box;
}

// ---- Balance Card ----
.balance-card {
  margin-top: 16px;
  border-radius: 15px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  min-height: 120px; /* 关键：确保卡片有高度，根据设计稿调整 */
}

.balance-card-inner {
  position: absolute;
  inset: 0;
  border-radius: 4px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    // background: #ed233c;
    background-image: url(../assets/home/border-out.png);
    background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
    background-repeat: no-repeat; /* 防止平铺 */
    opacity: 0.5;
  }

  &::after {
    content: '';
    position: absolute;
    top: 7px;
    left: 7px;
    right: 7px;
    bottom: 7px;
    border-radius: 4px;
    background-image: url(../assets/home/border-in.png);
    background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
    background-repeat: no-repeat; /* 防止平铺 */
  }
}

.balance-content {
  position: relative;
  z-index: 1;
}

.balance-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.balance-amount {
  margin-top: 14px;
  font-size: 32px;
  font-weight: 600;
  text-align: center;
  color: rgba(255, 255, 255, 0.95);
  font-family: 'DIN Alternate', sans-serif;
  line-height: 1;
}

.balance-notice {
  margin-top: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px 12px;
}

.notice-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.95);
  line-height: 1.57;
}

// ---- Info Section ----
.info-section {
  margin-top: 16px;
}

.info-section-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

// ---- Detail Card ----
.detail-card {
  margin-top: 12px;
  border-radius: 4px;
  padding: 10px;
  position: relative;
  background-image: url(../assets/home/detail-card-border.png);
  background-size: 100% 100%; /* 改为拉伸填满，不裁剪 */
  background-repeat: no-repeat; /* 防止平铺 */

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 4px;
    padding: 1px;
    // background: linear-gradient(97deg, #fff 2%, transparent 16%),
    //   linear-gradient(98deg, rgba(243, 40, 90, 1) 2%, rgba(255, 255, 255, 0.1) 98%);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
}

.detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  position: relative;
  z-index: 1;

  & + & {
    margin-top: 2px;
  }
}

.detail-label {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.detail-value {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 500;
  text-align: right;
}

.input-row {
  background: rgba(255, 255, 255, 0.06);
}

.input-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.amount-input {
  width: 100px;
  background: transparent;
  border: none;
  outline: none;
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  text-align: right;
  font-family: 'DIN Alternate', sans-serif;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.2);
  }
}

.input-unit {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}

// ---- Confirm Button ----
.confirm-btn {
  margin-top: 24px;
  width: 100%;
  height: 44px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.confirm-btn-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.confirm-btn-text {
  position: relative;
  z-index: 1;
  font-size: 15px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.95);
}

// ---- Toast ----
.toast {
  position: fixed;
  bottom: 110px;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px 20px;
  border-radius: 20px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 14px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 200;
}

// ---- Reinvest Button ----
.reinvest-btn {
  margin-top: 12px;
  opacity: 0.85;
}

// ---- Modal ----
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
  padding: 0 20px;
}

.modal-card {
  width: 100%;
  max-width: 390px;
  background: linear-gradient(180deg, #1a0510 0%, #11050f 100%);
  border: 1px solid rgba(243, 40, 90, 0.3);
  border-radius: 12px;
  padding: 20px 16px;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  margin-bottom: 12px;
}

.modal-hint {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.6;
  text-align: center;
  margin-bottom: 18px;
}

.modal-actions {
  display: flex;
  gap: 10px;
}

.modal-btn {
  flex: 1;
  height: 44px;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  font-size: 15px;
  font-weight: 500;

  &--cancel {
    color: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

  &--confirm {
    color: rgba(255, 255, 255, 0.95);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.modal-btn-bg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}

.modal-btn-text {
  position: relative;
  z-index: 1;
}
</style>
